// src/Hooks/useAuth.js
import { useDispatch, useSelector } from "react-redux";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  logoutUser,
  clearError,
  updateUser,
} from "../../src/Utils/authSlice";
import { authService } from "../services/authService";

export const useAuth = () => {
  const dispatch = useDispatch();
  const { user, token, isAuthenticated, loading, error } = useSelector(
    (state) => state.auth
  );

  const login = async (email, password, role = 2) => {
    try {
      dispatch(loginStart());
      const data = await authService.login(email, password, role);
      dispatch(loginSuccess(data));
      
      // Verify authentication was successful
      if (data.token) {
        return data;
      } else {
        throw new Error("No token received");
      }
    } catch (error) {
      console.error("Login error in hook:", error);
      
      // Extract meaningful error message
      let errorMessage = error.message || "Login failed";
      
      // Handle validation errors from backend
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (typeof serverMessage === 'object') {
          // Laravel validation errors format
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.data?.message) {
        const serverMessage = error.data.message;
        if (typeof serverMessage === 'object') {
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      }
      
      dispatch(loginFailure(errorMessage));
      throw new Error(errorMessage);
    }
  };

  const register = async (userData) => {
    try {
      dispatch(registerStart());
      const data = await authService.register(userData);
      dispatch(registerSuccess(data));
      return data;
    } catch (error) {
      console.error("Register error in hook:", error);
      
      // Extract meaningful error message
      let errorMessage = error.message || "Registration failed";
      
      // Handle validation errors from backend
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (typeof serverMessage === 'object') {
          // Laravel validation errors format
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      } else if (error.data?.message) {
        const serverMessage = error.data.message;
        if (typeof serverMessage === 'object') {
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      }
      
      // Handle specific validation errors from the error object itself
      if (error.message && typeof error.message === 'object') {
        const errorMessages = Object.values(error.message).flat();
        errorMessage = errorMessages.join(', ');
      }
      
      dispatch(registerFailure(errorMessage));
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await authService.logout(token);
    } finally {
      dispatch(logoutUser());
    }
  };

  const fetchUserProfile = async () => {
    try {
      const currentToken = token || authService.getToken();
      if (!currentToken) {
        throw new Error("No token found");
      }
      
      dispatch(loginStart());
      const userData = await authService.getProfile(currentToken);
      dispatch(updateUser(userData));
      return userData;
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      
      let errorMessage = error.message || "Failed to fetch profile";
      
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (typeof serverMessage === 'object') {
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      }
      
      if (errorMessage.includes("401") || errorMessage.includes("unauthorized") || errorMessage.includes("Unauthenticated")) {
        await logout();
      }
      
      throw new Error(errorMessage);
    }
  };

  const updateUserProfile = async (userData) => {
    try {
      const currentToken = token || authService.getToken();
      if (!currentToken) {
        throw new Error("No token found");
      }
      
      dispatch(loginStart());
      const updatedUser = await authService.updateProfile(userData, currentToken);
      dispatch(updateUser(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error("Update profile error:", error);
      
      let errorMessage = error.message || "Failed to update profile";
      
      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;
        if (typeof serverMessage === 'object') {
          const errorMessages = Object.values(serverMessage).flat();
          errorMessage = errorMessages.join(', ');
        } else {
          errorMessage = serverMessage;
        }
      }
      
      dispatch(loginFailure(errorMessage));
      throw new Error(errorMessage);
    }
  };

  const clearAuthError = () => {
    dispatch(clearError());
  };

  const updateUserData = (userData) => {
    dispatch(updateUser(userData));
  };

  return {
    user,
    token,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    logout,
    fetchUserProfile,
    updateUserProfile,
    clearAuthError,
    updateUserData,
  };
};